/** Whose duty is it — the predicate behind the crew view's Claim button.
 *
 *  ⚠️ THIS IS NOT THE AUTHORIZATION, AND IT IS DELIBERATELY NARROWER THAN ONE.
 *  The server decides claimability in `planWindow` (`refereeSlotState.ts`) plus
 *  `canSelfAssign`, and it knows things this cannot: the claim window, a
 *  ref-admin force-open, grade coverage, own-child CR, overlaps, the youth age
 *  cap. This answers only the question the crew view raised — is this row this
 *  team's own open duty — so a board that now renders the OPPOSING team's slots
 *  and Region's does not put a button on them.
 *
 *  ⚠️ AN EARLIER VERSION TOOK A LIST OF TEAM IDS AND HAD A `'Region'` BRANCH.
 *  Both were dead: `listMyTeams` groups slots by `responsibility` and hands each
 *  team its own, so the caller only ever has one team id in hand, and a
 *  `'Region'` slot cannot reach it. Worse, the Region branch CONTRADICTED
 *  `planWindow`, which refuses a Region slot carrying no `opened_at`. A shared
 *  predicate that disagrees with the server is a trap even when unreachable.
 *
 *  Region slots stay claimable on the Open board, which runs `annotateOpenForRef`
 *  over every slot it lists. My teams runs the same pass on the rows this
 *  predicate admits and on nothing else (`annotateTeamDuty`, 2026-09-18), so a
 *  Region row there carries no reasons to show under a button. */
export function isOwnTeamDutyOpen(
  slot: { responsibility: string; assignment_state: string },
  teamExternalId: string,
): boolean {
  return slot.assignment_state === 'unassigned' && slot.responsibility === teamExternalId
}

/** Display order for a game's officials: the center reads first, then the
 *  assistants in number order.
 *
 *  ⚠️ THE DATABASE CANNOT DO THIS. `referee_slots.position` is TEXT, so a SQL
 *  `ORDER BY position` sorts AR1, AR2, AR3, CR and puts the center LAST — which
 *  is why `crewQueryPlan` deliberately orders by kickoff only and leaves this
 *  to the render.
 *
 *  A position this does not know sorts after the ones it does, keeping the
 *  order it arrived in. `position` is a closed union in TypeScript but a bare
 *  TEXT column in D1, so an unrecognized value is a data question, not a reason
 *  to drop an official off a crew list. */
const CREW_ORDER: readonly string[] = ['CR', 'AR1', 'AR2', 'AR3']

/** PURE. Comparator for two positions in crew order, for a sort that ranks on
 *  something else first.
 *
 *  ⚠️ EXPORTED BECAUSE THIS RULE HAD ALREADY DRIFTED BEFORE IT HAD A HOME. Two
 *  private copies existed — `coach-spa`'s referee view and
 *  `referee-spa/lib/adminSlotFilters.ts` — and the second was missing `AR3`
 *  entirely, so the ref-admin table sorted a young game's fourth official
 *  in with the unknowns. Anything ordering positions consumes this. */
export function comparePosition(a: { position: string }, b: { position: string }): number {
  const rank = (position: string): number => {
    const i = CREW_ORDER.indexOf(position)
    return i === -1 ? CREW_ORDER.length : i
  }
  return rank(a.position) - rank(b.position)
}

export function orderCrew<T extends { position: string }>(slots: readonly T[]): T[] {
  // `toSorted` is not available in the Workers runtime baseline this package
  // targets, so copy then sort. Array#sort is stable, which is what keeps an
  // unknown position in arrival order.
  return [...slots].sort(comparePosition)
}

/** A game id → the officials on that game, each crew already in display order.
 *  Keyed rather than nested on the game so a board that already knows its games
 *  (from the viewer's own slots, or from their teams' duty) can look up a crew
 *  without re-deriving the game list. */
export type CrewByGame<T> = Record<string, T[]>

/** PURE. Group crew rows by game, ordering each game's officials.
 *
 *  ⚠️ ORDERING BELONGS HERE, not in the query. `crewQueryPlan` sorts by kickoff
 *  so the boards can render chronologically; within one game SQLite returns
 *  rows in whatever order it likes, and `position` cannot be sorted in SQL
 *  without putting the center last (see `orderCrew`). */
export function groupCrewByGame<T extends { game_id: string; position: string }>(
  slots: readonly T[],
): CrewByGame<T> {
  const out: CrewByGame<T> = {}
  for (const slot of slots) (out[slot.game_id] ??= []).push(slot)
  for (const gameId of Object.keys(out)) out[gameId] = orderCrew(out[gameId]!)
  return out
}
